// Roster spatial-first tours (U5): inline affordance to append a new
// touring_identity entry to a tour's vocabulary without leaving the load-in
// table. Operator picks "+ Add new identity to tour…" in the per-service
// dropdown; this modal opens; on success the caller refreshes the dropdown
// options + pre-selects the new label for the originating service row.
//
// Error surfaces match the U1 PATCH /api/tours/{id} contract:
//   - 409 `duplicate vocabulary label…` or `tour_name_conflict`
//   - 400 validation errors (label too long, disallowed chars, invalid tier)
// Both render inline; the dialog stays open so the operator can correct.

import { useState } from "react";
import { addTourVocabularyEntry } from "@/lib/api";
import { ApiError } from "@/lib/api";
import type { TourVocabularyEntry } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  tourId: string;
  open: boolean;
  onClose: () => void;
  onAdded: (entry: TourVocabularyEntry) => void;
}

export function VocabularyInlineAddDialog({ tourId, open, onClose, onAdded }: Props) {
  const [label, setLabel] = useState("");
  const [tier, setTier] = useState<"stage" | "house">("stage");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setLabel("");
    setTier("stage");
    setSaving(false);
    setErr(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !saving) {
      reset();
      onClose();
    }
  };

  const friendlyError = (e: unknown): string => {
    if (e instanceof ApiError) {
      const body = e.body as { error?: string } | undefined;
      const msg = body?.error ?? "";
      if (e.status === 409) {
        if (msg.startsWith("duplicate vocabulary label")) {
          return "That label already exists in this tour's vocabulary.";
        }
        return msg || "Conflict — another change to this tour landed first; refresh and retry.";
      }
      if (e.status === 400) {
        return msg || "Couldn't add identity (validation failed).";
      }
      return msg || `Server error ${e.status}`;
    }
    return String(e);
  };

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setErr("Label is required.");
      return;
    }
    setErr(null);
    setSaving(true);
    const entry: TourVocabularyEntry = { label: trimmed, tier };
    try {
      await addTourVocabularyEntry(tourId, entry);
      onAdded(entry);
      reset();
      onClose();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add identity to tour vocabulary</DialogTitle>
          <DialogDescription>
            New identities apply to this tour only. Pick a tier so the
            spatial-first clusterer can group services correctly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vocab-label">Label</Label>
            <Input
              id="vocab-label"
              value={label}
              placeholder="e.g. FOH, Stage L Sub, Monitor World"
              maxLength={80}
              disabled={saving}
              autoFocus
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !saving) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Tier</Label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="vocab-tier"
                  value="stage"
                  checked={tier === "stage"}
                  disabled={saving}
                  onChange={() => setTier("stage")}
                />
                Stage
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="vocab-tier"
                  value="house"
                  checked={tier === "house"}
                  disabled={saving}
                  onChange={() => setTier("house")}
                />
                House
              </label>
            </div>
          </div>

          {err && <div className="text-destructive text-sm">{err}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
                Saving…
              </span>
            ) : (
              "Add identity"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
